import { ArrayUtil, ModelRow, StringUtil } from "@mcleod/core";
import {
    ChangeEvent, ClickEvent, DataSource, DataSourceMode, Label, Panel, Switch, Table, TableRow, TableRowDisplayEvent,
    TableRowMode, TableRowModeChangeEvent, Textbox
} from "@mcleod/components";
import { CommonDialogs } from "@mcleod/common";
import { EditSendToLanes } from "./EditSendToLanes";

interface LanePreferenceContactManagerProps {
    lanePreferenceTable: Table;
    sendToContactsTable: Table;
    carrierContactsTable: Table;
    parentDataSource: DataSource;
}

export class LanePreferenceContactManager {
    private parentDataSource: DataSource;
    private lanePreferenceTable: Table;
    private sendToContactsTable: Table;
    private carrierContactsTable: Table;
    private lanePreferenceDisplayListener: (event: TableRowDisplayEvent) => void;
    private lanePreferenceRowModeChangeListener: (event: TableRowModeChangeEvent) => void;

    constructor(props: LanePreferenceContactManagerProps) {
        this.lanePreferenceTable = props.lanePreferenceTable;
        this.sendToContactsTable = props.sendToContactsTable;
        this.carrierContactsTable = props.carrierContactsTable;
        this.parentDataSource = props.parentDataSource;

        this.parentDataSource.addAfterSearchPlusChildrenListener(() => this.syncLaneContactsAndPreferences())
        this.parentDataSource.addAfterExecutionListener(() => this.syncLaneContactsAndPreferences());

        this.sendToContactsTable.addRowDisplayListener(event => this.sendToRowDisplay(event));
        this.lanePreferenceTable.addRowCreateListener((event) => {
            if (this.lanePreferenceTable.quickAddRow !== event.getTableRow()) {
                this.syncLaneContactsAndPreferences();
            }
        });
        this.sendToContactsTable.addRowCreateListener((event) => {
            if (this.sendToContactsTable.quickAddRow !== event.getTableRow()) {
                this.syncLaneContactsAndPreferences();
            }
        });
        this.lanePreferenceTable.doOnRowDelete = (row) => this.removeLaneFromSendTo(row);
        this.sendToContactsTable.doOnRowDelete = (row) => this.removeSendToFromLane(row);
        this.carrierContactsTable.doOnRowDelete = (row) => this.removeOverrideContactFromLane(row.data);
        this.carrierContactsTable.addRowModeChangeListener((event: TableRowModeChangeEvent) => this.contactRowModeChange(event));
        this.carrierContactsTable.addRowDisplayListener((event: TableRowDisplayEvent) => this.contactRowDisplay(event));
    }

    sendToRowDisplay(event: TableRowDisplayEvent) {
        if (DataSourceMode.SEARCH === event.getTable().dataSource.mode) return;
        const tableRow = event.getTableRow();
        const labelLaneCount = tableRow.findComponentById("labelLaneCount") as Label;
        if (labelLaneCount && !labelLaneCount.hasClickListeners()) {
            labelLaneCount.addClickListener((event: ClickEvent) => {
                const labelLaneCount = event.target as Label;
                const tableRow = TableRow.getContainingTableRow(labelLaneCount);
                if (tableRow != null) {
                    const modelRow = tableRow.data as ModelRow;
                    if (modelRow != null) {
                        const contactName = modelRow.getString("sendto_name");
                        const laneNumber = modelRow.getString("lane_number", "");
                        EditSendToLanes.showSlideout(
                            this.parentDataSource.activeRow?.getString('id'),
                            contactName,
                            laneNumber,
                            (lanes) => {
                                modelRow.set("lane_number", lanes);
                                this.sendToContactsTable.redisplaySingleRow(tableRow.index, modelRow, false, true);
                                this.syncLaneContactsAndPreferences();
                            },
                            this.lanePreferenceTable.allRows.map(row => row.data)
                        );
                    }
                }
            });
        }
    }

    removeLaneFromSendTo(row: TableRow) {
        const laneNumber = row.data.getNumber('lane_number').toString();
        this.sendToContactsTable.allRows.forEach((tableRow) => {
            const sendToRow = tableRow.data;
            const sendToLanes = sendToRow.getString('lane_number').split(',');
            if (sendToLanes.includes(laneNumber)) {
                const newSendToLanes = sendToLanes.filter((lane) => lane !== laneNumber).join(',');
                if (StringUtil.isEmptyString(newSendToLanes)) {
                    sendToRow.delete();
                } else {
                    sendToRow.set('lane_number', newSendToLanes);
                    this.sendToContactsTable.redisplaySingleRow(tableRow.index, sendToRow, false, true);
                }
            }
        });
    }

    removeSendToFromLane(row: TableRow): void {
        this.syncLaneContactsAndPreferences();
    }

    removeOverrideContactFromLane(contactRow: ModelRow): void {
        const contactId = contactRow.getString('id');
        this.lanePreferenceTable.allRows.forEach((tableRow) => {
            const modelRow = tableRow.data as ModelRow;
            if (StringUtil.equalsIgnoreCase(modelRow.getString('topmatch_override'), contactId)) {
                modelRow.set('topmatch_override', null);
                this.lanePreferenceTable.redisplaySingleRow(tableRow.index, modelRow, false, true);
            }
        });
    }

    contactRowModeChange(event: TableRowModeChangeEvent) {
        if (event.oldMode === TableRowMode.UPDATE) {
            const modelRow = event.getTableRow().data as ModelRow;
            const isActiveChanged = !!modelRow.getChangedData()?.is_active;
            if (isActiveChanged && modelRow.getBoolean("is_active", false) === false) {
                this.removeOverrideContactFromLane(modelRow);
            }
        }
    }

    contactRowDisplay(event: TableRowDisplayEvent) {
        if (DataSourceMode.SEARCH === event.getTable().dataSource.mode) return;
        const tableRow = event.getTableRow();
        const switchActive = tableRow.findComponentById("switchActive") as Switch;
        switchActive?.addChangeListener((event: ChangeEvent) => {
            if (!event.newValue) { //set to inactive
                const name = tableRow.data?.getString("name");
                const email = tableRow.data?.getString("email");
                const matchingPnnSendtos = this.sendToContactsTable.data?.filter(sendTo =>
                    (sendTo.isNull('unsubscribe_date') || new Date(sendTo.getDate('unsubscribe_date')).getTime() > new Date().getTime())
                    && sendTo.getString("sendto_name").toUpperCase() === name.toUpperCase()
                    && sendTo.getString("sendto_detail").toUpperCase() === email.toUpperCase()
                );
                if (matchingPnnSendtos.length > 0) {
                    const dialogText = "Do you want to unsubscribe the contact from available load reports?";
                    CommonDialogs.showYesNo(dialogText, "Deactivate Contact").then(result => {
                        if (result) { //yes
                            const today = new Date();
                            matchingPnnSendtos.forEach(sendTo => {
                                sendTo.set("unsubscribe_date", today);
                            });
                            this.syncLaneContactsAndPreferences()
                            this.lanePreferenceTable.dataSource.displayDataInBoundComponents();
                        } else if (result === undefined) { //X (cancel)
                            switchActive.checked = true;
                        }
                    });
                }
            }
        });
    }

    public syncLaneContactsAndPreferences() {
        const lanePreferenceList = this.lanePreferenceTable.dataSource.data;
        const laneSendToList = this.sendToContactsTable.dataSource.data;

        //determine contacts for each lane
        const laneContactMap = [];
        const uniqueLanes = new Set<number>(lanePreferenceList.map((lanePreference) => lanePreference.getNumber('lane_number')));
        uniqueLanes.forEach(_ => laneContactMap.push([]));
        laneSendToList.forEach((sendTo) => this.populateContactMap(sendTo, laneContactMap));

        if (this.lanePreferenceDisplayListener) {//Cleanup old listener with out of sync contact Map
            this.lanePreferenceTable.removeRowDisplayListener(this.lanePreferenceDisplayListener);
            this.lanePreferenceTable.removeRowModeChangeListener(this.lanePreferenceRowModeChangeListener);
        }
        this.lanePreferenceDisplayListener = (event) => this.syncLaneWithContactMap(event.getTableRow(), laneContactMap);
        this.lanePreferenceRowModeChangeListener = (event) => {
            if (event.oldMode === TableRowMode.UPDATE) {
                this.syncLaneWithContactMap(event.getTableRow(), laneContactMap);
            }
        }
        this.lanePreferenceTable.addRowDisplayListener(this.lanePreferenceDisplayListener);
        this.lanePreferenceTable.addRowModeChangeListener(this.lanePreferenceRowModeChangeListener);

        this.lanePreferenceTable.allRows.forEach((tableRow) => {
            if (tableRow.populatedDOM) { //first row of table frequently loads before the rest of the table
                this.lanePreferenceTable.redisplaySingleRow(tableRow.index, tableRow.data, false, true);
            }
        });

        this.lanePreferenceTable.allRows.forEach((tableRow) => {
            const modelRow: ModelRow = tableRow.data;
            if (!modelRow.isNull('topmatch_override')) {
                const topMatchOverrideId = modelRow.getString('topmatch_override');
                const contactRow = this.carrierContactsTable.data.find((row) => row.getString('id') === topMatchOverrideId);
                if (contactRow) {
                    modelRow.setLookupModelData('topmatch_override', contactRow);
                }
            }
        });
    }

    populateContactMap(sendTo: ModelRow<any>, laneContactMap: any[]): void {
        const unsubscribeDate = sendTo.getDate('unsubscribe_date');
        if (unsubscribeDate && new Date(unsubscribeDate).getTime() < new Date().getTime()) {
            return;
        }
        const name = sendTo.getString('sendto_name');
        const email = sendTo.getString('sendto_detail');
        const sequence = sendTo.getNumber('sequence');
        const contact = { name, email, sequence };
        const lanes = sendTo.get('lane_number');
        if (name) {
            if (StringUtil.equalsIgnoreCase('*', lanes)) {
                laneContactMap.forEach((list) => {
                    list.push(contact);
                });
            } else {
                const laneNumbers = lanes.split(',');
                laneNumbers.forEach((lane) => {
                    if (laneContactMap[lane - 1]) laneContactMap[lane - 1].push(contact);
                });
            }
        }
    }

    syncLaneWithContactMap(tableRow: TableRow, laneContactMap: any[]) {
        const modelRow: ModelRow = tableRow.data
        const laneNumber = modelRow.getNumber('lane_number');
        const labelLaneCount = tableRow.findComponentById("labelContactCount") as Label;
        const textboxPrimaryContact = tableRow.findComponentById("textboxPrimaryContact") as Textbox;
        const labelEmail = tableRow.findComponentById("labelEmail") as Label;
        const textboxTopMatchOverride = tableRow.findComponentById("textboxTopMatchOverride") as Textbox;
        if (labelLaneCount && this.lanePreferenceTable.quickAddRow !== tableRow) {
            const contacts = laneContactMap[laneNumber - 1] ?? []
            labelLaneCount.text = `${contacts.length} contact(s)`;

            if (contacts.length > 1) {
                contacts.sort((a, b) => a.sequence - b.sequence);
            }
            if (labelEmail) {
                labelEmail.text = contacts[0]?.email;
            }
            textboxTopMatchOverride.visible = !modelRow.isNull('topmatch_override');
            if (!textboxTopMatchOverride.visible) {
                labelEmail.visible = contacts.length > 0;
                textboxPrimaryContact.visible = !labelEmail.visible;
                if (!labelEmail.visible) {
                    textboxPrimaryContact.text = this.carrierContactsTable.allRows[0]?.data.getString('email');
                }
            }
            labelLaneCount.tooltipDynamicContent = () => this.buildPanelContactTooltip(contacts);
        }
    }

    buildPanelContactTooltip(contacts: {name:string, email:string, sequence:number}[]): Panel {
        if (ArrayUtil.isEmptyArray(contacts)) {
            return null;
        }
        const panelContactTooltip = new Panel({ id: "panelContactTooltip" });
        panelContactTooltip.add(new Label({ text: "Contacts", fontBold: true, marginBottom: 10 }));
        contacts.forEach(contact => {
            panelContactTooltip.add(new Label({ text: contact.name }));
        });
        return panelContactTooltip;
    }
}
